import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Sparkles, Users, TrendingUp, BarChart3, Wallet, Target, ArrowRight, Rocket, Heart, Star } from 'lucide-react';
import { toast } from 'sonner';
import slicelogo from '@/assets/slice-logo-v2.png';
import { ComprehensiveBetaForm } from '@/components/beta/ComprehensiveBetaForm';
import { mapSupabaseError } from '@/utils/security';

export default function BetaSignupPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Check if user is already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        console.log('BetaSignupPage: User already authenticated, redirecting to home');
        navigate('/', { replace: true });
      } else {
        setCheckingAuth(false);
      }
    };

    checkAuth();
  }, [navigate]);

  // Show loading state while checking authentication
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/5 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-muted-foreground">Checking authentication...</p>
        </div>
      </div>
    );
  }

  const handleFormSubmit = async (data: any) => {
    setIsSubmitting(true);
    try {
      console.log('Starting beta signup process...', data);

      // Call the edge function to handle signup (no confirmation email)
      const { data: response, error } = await supabase.functions.invoke('beta-signup', {
        body: data
      });

      if (error) throw error;
      if (!response?.success) throw new Error(response?.error || 'Failed to submit application');

      console.log('Beta application submitted successfully');

      toast.success(
        'Application submitted! Your account is created and pending approval.',
        { duration: 5000 }
      );

      navigate('/pending-approval', { 
        state: { email: data.email } 
      });
    } catch (error: any) {
      // Known user-facing cases first, then fall back to the shared mapper
      // so we never leak raw DB error strings (which may contain column or
      // constraint names) to end users.
      let errorMessage: string;
      if (error?.message?.includes('User already registered')) {
        errorMessage = 'An account with this email already exists.';
      } else if (error?.message?.includes('duplicate') || error?.message?.includes('23505')) {
        errorMessage = 'An application with this email already exists.';
      } else {
        errorMessage = mapSupabaseError(error, 'An error occurred. Please try again.');
      }

      toast.error(errorMessage, { duration: 5000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/5">
      {/* Hero Section */}
      <section className="relative overflow-hidden border-b">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5" />
        <div className="container relative mx-auto px-4 py-16 md:py-24">
          <div className="flex flex-col items-center text-center space-y-8">
            <img src={slicelogo} alt="TheSlice Logo" className="h-16 w-auto" />
            <div className="space-y-4 max-w-3xl">
              <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
                Join TheSlice Beta Program
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground">
                Shape the future of sports business partnerships
              </p>
            </div>
            <Button 
              size="lg" 
              className="text-lg px-8 py-6"
              onClick={() => document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Apply for Beta Access <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
          Why Join the Beta?
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: Rocket, title: 'Early Access', desc: 'Be first to use cutting-edge features' },
            { icon: Heart, title: 'Shape the Product', desc: 'Your feedback directly influences development' },
            { icon: Users, title: 'Exclusive Community', desc: 'Connect with other beta testers' },
            { icon: Star, title: 'Special Perks', desc: 'Premium features & priority support' },
            { icon: TrendingUp, title: 'Direct Impact', desc: 'See your suggestions implemented' },
            { icon: Target, title: 'Founder Benefits', desc: 'Exclusive perks and recognition as an early supporter' },
          ].map((item, i) => (
            <Card key={i} className="border-2 hover:border-primary/50 transition-all hover:shadow-lg">
              <CardHeader>
                <item.icon className="h-10 w-10 text-primary mb-2" />
                <CardTitle>{item.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">{item.desc}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Features Showcase */}
      <section className="bg-muted/30 py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            What You'll Get Access To
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Sparkles, title: 'SliceAI', desc: 'AI-powered deal recommendations and insights', comingSoon: false },
              { icon: Users, title: 'Social Feed', desc: 'Connect and collaborate with talent and brands', comingSoon: true },
              { icon: Target, title: 'Deal Marketplace', desc: 'Find and close partnership opportunities', comingSoon: false },
              { icon: BarChart3, title: 'Analytics Vault', desc: 'Track performance and ROI', comingSoon: true },
              { icon: Wallet, title: 'Wallet System', desc: 'Seamless payments and transactions', comingSoon: true },
              { icon: TrendingUp, title: 'Smart Matching', desc: 'AI-driven talent-brand pairing', comingSoon: false },
            ].map((feature, i) => (
              <Card key={i} className="hover:shadow-lg transition-all relative">
                {feature.comingSoon && (
                  <div className="absolute top-3 right-3 bg-secondary text-secondary-foreground text-xs font-semibold px-2.5 py-1 rounded-full">
                    Coming Soon
                  </div>
                )}
                <CardHeader>
                  <feature.icon className="h-8 w-8 text-secondary mb-2" />
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
          How It Works
        </h2>
        <div className="max-w-3xl mx-auto space-y-8">
          {[
            { step: '1', title: 'Apply', desc: 'Fill out the beta application form below' },
            { step: '2', title: 'Wait', desc: 'We review applications within 24-48 hours' },
            { step: '3', title: 'Get Approved', desc: 'Receive email confirmation with next steps' },
            { step: '4', title: 'Explore', desc: 'Complete onboarding and start using the platform' },
            { step: '5', title: 'Share Feedback', desc: 'Help us improve with your insights' },
          ].map((item) => (
            <div key={item.step} className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold">
                {item.step}
              </div>
              <div className="flex-1 pt-1">
                <h3 className="text-xl font-semibold mb-1">{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Application Form */}
      <section id="application-form" className="bg-muted/30 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <Card className="border-2">
              <CardHeader className="text-center">
                <CardTitle className="text-3xl mb-2">Apply for Beta Access</CardTitle>
                <CardDescription className="text-base">
                  Complete your full profile as part of the application
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ComprehensiveBetaForm onSubmit={handleFormSubmit} isSubmitting={isSubmitting} />
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
          Frequently Asked Questions
        </h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border rounded-lg px-6">
              <AccordionTrigger className="text-lg font-semibold">
                How long does approval take?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                We review applications within 24-48 hours. You'll receive an email notification once your application has been reviewed.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border rounded-lg px-6">
              <AccordionTrigger className="text-lg font-semibold">
                Is the beta free?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Yes! Beta access is completely free, and beta users will receive lifetime access to premium features as a thank you for helping us shape the platform.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border rounded-lg px-6">
              <AccordionTrigger className="text-lg font-semibold">
                What if I have technical issues?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Beta users get priority support! Use the feedback button in the app or email us directly. We're here to help and want to hear about any issues you encounter.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border rounded-lg px-6">
              <AccordionTrigger className="text-lg font-semibold">
                Can I invite others?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Once you're approved and active in the beta, you'll receive invite codes to share with colleagues and friends.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="border rounded-lg px-6">
              <AccordionTrigger className="text-lg font-semibold">
                When will TheSlice fully launch?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                We're currently in private beta, refining features based on user feedback. A public launch is planned for Q2 2025, but beta users will maintain their exclusive benefits.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-6" className="border rounded-lg px-6">
              <AccordionTrigger className="text-lg font-semibold">
                What data do you collect during beta?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                We collect usage data to improve the platform, but we never share your personal information. All data is encrypted and handled according to our Privacy Policy.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-muted/30 py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <img src={slicelogo} alt="TheSlice" className="h-8 w-auto" />
              <span className="text-sm text-muted-foreground">© 2025 TheSlice. All rights reserved.</span>
            </div>
            <div className="flex gap-6 text-sm">
              <Link to="/terms" className="text-muted-foreground hover:text-primary transition-colors">
                Terms
              </Link>
              <Link to="/privacy" className="text-muted-foreground hover:text-primary transition-colors">
                Privacy
              </Link>
              <Link to="/auth" className="text-muted-foreground hover:text-primary transition-colors">
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
